import { useEffect, useState } from "react";
import { Car, Clock3, FileText, Plus, TicketCheck } from "lucide-react";
import { toast } from "sonner";

import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Loading } from "../../components/shared";

const emptyVehicle = {
  unit_id: "",
  registration_number: "",
  vehicle_type: "CAR",
  make: "",
  model: "",
  colour: "",
  fuel_type: "",
  ev: false,
};

function Status({ value }) {
  const color =
    {
      APPROVED: "bg-emerald-100 text-emerald-800",
      PENDING_VERIFICATION: "bg-amber-100 text-amber-800",
      REJECTED: "bg-rose-100 text-rose-800",
      ACTIVE: "bg-emerald-100 text-emerald-800",
      WAITING: "bg-amber-100 text-amber-800",
    }[value] || "bg-slate-100 text-slate-700";
  return (
    <span
      className={`rounded-full px-2 py-1 text-xs font-semibold ${color}`}
      data-testid={`resident-parking-status-${String(value).toLowerCase()}`}
    >
      {String(value || "UNKNOWN").replaceAll("_", " ")}
    </span>
  );
}

export default function ResidentParking() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("vehicles");
  const [vehicle, setVehicle] = useState(emptyVehicle);
  const [waiting, setWaiting] = useState({
    unit_id: "",
    vehicle_id: "",
    requested_slot_type: "CAR",
    requested_vehicle_type: "CAR",
    notes: "",
  });
  const [documentFiles, setDocumentFiles] = useState({});

  const load = async () => {
    try {
      const response = await api.get("/resident-parking/bootstrap");
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load parking");
      setData({
        units: [],
        vehicles: [],
        allocations: [],
        waiting: [],
        temporary: [],
        passes: [],
        settings: {},
      });
    }
  };
  useEffect(() => {
    load();
  }, []);
  const saveVehicle = async () => {
    try {
      await api.post("/resident-parking/vehicles", vehicle);
      toast.success("Vehicle submitted");
      setVehicle(emptyVehicle);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not submit vehicle");
    }
  };
  const joinWaiting = async () => {
    try {
      await api.post("/resident-parking/waiting-list", waiting);
      toast.success("Parking request added");
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not add parking request",
      );
    }
  };
  const uploadDocument = async (vehicleId) => {
    const item = documentFiles[vehicleId];
    if (!item?.file) {
      toast.error("Choose a document before uploading");
      return;
    }
    const form = new FormData();
    form.append("file", item.file);
    form.append("category", item.category || "Other");
    try {
      await api.post(`/resident-parking/vehicles/${vehicleId}/documents`, form);
      toast.success("Vehicle document uploaded securely");
      setDocumentFiles({
        ...documentFiles,
        [vehicleId]: { category: "RC", file: null },
      });
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not upload vehicle document",
      );
    }
  };
  if (!data) return <Loading />;
  return (
    <div data-testid="resident-parking-page">
      <header className="border-b border-slate-200 pb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          Vehicle & parking
        </p>
        <h1 className="mt-2 font-display text-4xl font-bold text-slate-900">
          Parking & Vehicles
        </h1>
        <p
          className="mt-2 text-sm text-slate-600"
          data-testid="resident-parking-timezone"
        >
          Society time: {data.timezone}
        </p>
      </header>
      <div
        className="mt-6 flex flex-wrap gap-2"
        data-testid="resident-parking-tabs"
      >
        {[
          ["vehicles", "My Vehicles"],
          ["parking", "My Parking"],
          ["waiting", "Waiting List"],
          ["passes", "Passes"],
        ].map(([value, label]) => (
          <Button
            data-testid={`resident-parking-tab-${value}`}
            key={value}
            onClick={() => setTab(value)}
            size="sm"
            variant={tab === value ? "default" : "outline"}
          >
            {label}
          </Button>
        ))}
      </div>
      {tab === "vehicles" && (
        <section className="mt-6 grid gap-6 xl:grid-cols-[390px_1fr]">
          <div
            className="border bg-white p-5"
            data-testid="resident-vehicle-form"
          >
            <h2 className="font-display text-xl font-semibold">Add vehicle</h2>
            <div className="mt-4 grid gap-3">
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="resident-vehicle-unit"
                onChange={(event) =>
                  setVehicle({ ...vehicle, unit_id: event.target.value })
                }
                value={vehicle.unit_id}
              >
                <option value="">Select authorized unit</option>
                {data.units.map((item) => (
                  <option key={item.unit_id} value={item.unit_id}>
                    {item.unit_number}
                  </option>
                ))}
              </select>
              <Input
                data-testid="resident-vehicle-registration"
                onChange={(event) =>
                  setVehicle({
                    ...vehicle,
                    registration_number: event.target.value.toUpperCase(),
                  })
                }
                placeholder="MH 48 AB 1234"
                value={vehicle.registration_number}
              />
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="resident-vehicle-type"
                onChange={(event) =>
                  setVehicle({ ...vehicle, vehicle_type: event.target.value })
                }
                value={vehicle.vehicle_type}
              >
                <option value="CAR">Car</option>
                <option value="TWO_WHEELER">Two-wheeler</option>
                <option value="OTHER">Other</option>
              </select>
              <Input
                data-testid="resident-vehicle-make"
                onChange={(event) =>
                  setVehicle({ ...vehicle, make: event.target.value })
                }
                placeholder="Make"
                value={vehicle.make}
              />
              <Input
                data-testid="resident-vehicle-model"
                onChange={(event) =>
                  setVehicle({ ...vehicle, model: event.target.value })
                }
                placeholder="Model"
                value={vehicle.model}
              />
              <Input
                data-testid="resident-vehicle-colour"
                onChange={(event) =>
                  setVehicle({ ...vehicle, colour: event.target.value })
                }
                placeholder="Colour"
                value={vehicle.colour}
              />
            </div>
            <label className="mt-4 flex items-center gap-2 text-sm">
              <input
                checked={vehicle.ev}
                data-testid="resident-vehicle-ev"
                onChange={(event) =>
                  setVehicle({ ...vehicle, ev: event.target.checked })
                }
                type="checkbox"
              />
              Electric vehicle
            </label>
            <Button
              className="mt-4"
              data-testid="resident-vehicle-save"
              onClick={saveVehicle}
            >
              <Plus className="h-4 w-4" />
              Submit vehicle
            </Button>
          </div>
          <div className="space-y-3" data-testid="resident-vehicles-list">
            {data.vehicles.length === 0 ? (
              <p className="border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-600">
                No vehicles registered yet.
              </p>
            ) : (
              data.vehicles.map((item) => (
                <article
                  className="border bg-white p-4"
                  data-testid={`resident-vehicle-${item.vehicle_id}`}
                  key={item.vehicle_id}
                >
                  <div className="flex flex-wrap justify-between gap-3">
                    <div>
                      <h2 className="font-display text-lg font-semibold">
                        {item.registration_number}
                      </h2>
                      <p className="text-sm text-slate-600">
                        {item.vehicle_type} ·{" "}
                        {[item.make, item.model, item.colour]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      <p className="mt-1 text-sm text-slate-600">
                        Unit {item.unit_snapshot?.unit_number}
                      </p>
                      <div className="mt-4 flex flex-wrap items-end gap-2">
                        <select
                          className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                          data-testid={`resident-vehicle-document-category-${item.vehicle_id}`}
                          onChange={(event) =>
                            setDocumentFiles({
                              ...documentFiles,
                              [item.vehicle_id]: {
                                ...documentFiles[item.vehicle_id],
                                category: event.target.value,
                              },
                            })
                          }
                          value={
                            documentFiles[item.vehicle_id]?.category || "RC"
                          }
                        >
                          <option value="RC">RC</option>
                          <option value="Insurance">Insurance</option>
                          <option value="PUC">PUC</option>
                          <option value="Parking Undertaking">
                            Parking undertaking
                          </option>
                          <option value="Other">Other</option>
                        </select>
                        <input
                          data-testid={`resident-vehicle-document-file-${item.vehicle_id}`}
                          onChange={(event) =>
                            setDocumentFiles({
                              ...documentFiles,
                              [item.vehicle_id]: {
                                ...documentFiles[item.vehicle_id],
                                file: event.target.files?.[0] || null,
                              },
                            })
                          }
                          type="file"
                        />
                        <Button
                          data-testid={`resident-vehicle-document-upload-${item.vehicle_id}`}
                          onClick={() => uploadDocument(item.vehicle_id)}
                          size="sm"
                          variant="outline"
                        >
                          Upload document
                        </Button>
                      </div>
                      {(item.documents || []).length > 0 && (
                        <div
                          className="mt-4 space-y-1"
                          data-testid={`resident-vehicle-documents-${item.vehicle_id}`}
                        >
                          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                            Uploaded documents
                          </p>
                          {item.documents.map((doc) => (
                            <a
                              className="flex items-center gap-2 text-sm text-blue-700 hover:underline"
                              data-testid={`resident-vehicle-document-${doc.vehicle_document_id}`}
                              href={`${API}/resident-parking/vehicles/${item.vehicle_id}/documents/${doc.vehicle_document_id}`}
                              key={doc.vehicle_document_id}
                              rel="noreferrer"
                              target="_blank"
                            >
                              <FileText className="h-4 w-4" />
                              {doc.category} · {doc.filename}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    <Status value={item.verification_status} />
                  </div>
                </article>
              ))
            )}
          </div>
        </section>
      )}
      {tab === "parking" && (
        <section
          className="mt-6 space-y-3"
          data-testid="resident-current-parking"
        >
          {data.allocations.length === 0 ? (
            <p className="border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-600">
              No current parking allocation.
            </p>
          ) : (
            data.allocations.map((item) => (
              <article className="border bg-white p-5" key={item.allocation_id}>
                <h2 className="font-display text-xl font-semibold">
                  {item.slot_snapshot?.slot_number}
                </h2>
                <p className="mt-2 text-sm text-slate-600">
                  {item.slot_snapshot?.location || "Society parking"} · Unit{" "}
                  {item.unit_snapshot?.unit_number}
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  {item.start_date}
                  {item.end_date ? ` to ${item.end_date}` : " · Permanent"}
                </p>
              </article>
            ))
          )}
        </section>
      )}
      {tab === "waiting" && (
        <section className="mt-6 grid gap-6 xl:grid-cols-[390px_1fr]">
          <div
            className="border bg-white p-5"
            data-testid="resident-parking-wait-form"
          >
            <h2 className="font-display text-xl font-semibold">
              Request parking
            </h2>
            <div className="mt-4 grid gap-3">
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="resident-wait-unit"
                onChange={(event) =>
                  setWaiting({ ...waiting, unit_id: event.target.value })
                }
                value={waiting.unit_id}
              >
                <option value="">Select authorized unit</option>
                {data.units.map((item) => (
                  <option key={item.unit_id} value={item.unit_id}>
                    {item.unit_number}
                  </option>
                ))}
              </select>
              <select
                className="rounded-md border border-slate-300 px-3 py-2"
                data-testid="resident-wait-vehicle"
                onChange={(event) =>
                  setWaiting({ ...waiting, vehicle_id: event.target.value })
                }
                value={waiting.vehicle_id}
              >
                <option value="">No vehicle linked</option>
                {data.vehicles
                  .filter((item) => item.verification_status === "APPROVED")
                  .map((item) => (
                    <option key={item.vehicle_id} value={item.vehicle_id}>
                      {item.registration_number}
                    </option>
                  ))}
              </select>
              <Textarea
                data-testid="resident-wait-notes"
                onChange={(event) =>
                  setWaiting({ ...waiting, notes: event.target.value })
                }
                placeholder="Optional note"
                value={waiting.notes}
              />
            </div>
            <Button
              className="mt-4"
              data-testid="resident-wait-save"
              disabled={!data.settings.waiting_list_enabled}
              onClick={joinWaiting}
            >
              <Clock3 className="h-4 w-4" />
              Join waiting list
            </Button>
          </div>
          <div className="space-y-3" data-testid="resident-waiting-list">
            {data.waiting.length === 0 ? (
              <p className="border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-600">
                No active parking requests.
              </p>
            ) : (
              data.waiting.map((item) => (
                <article className="border bg-white p-4" key={item.waiting_id}>
                  <div className="flex justify-between gap-2">
                    <span>Request #{item.queue_sequence}</span>
                    <Status value={item.status} />
                  </div>
                </article>
              ))
            )}
          </div>
        </section>
      )}
      {tab === "passes" && (
        <section
          className="mt-6 space-y-3"
          data-testid="resident-parking-passes"
        >
          {data.passes.length === 0 ? (
            <p className="border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-600">
              No active parking pass.
            </p>
          ) : (
            data.passes.map((item) => (
              <article className="border bg-white p-4" key={item.pass_id}>
                <TicketCheck className="h-5 w-5 text-blue-700" />
                <p className="mt-2 font-mono font-semibold">
                  {item.pass_number}
                </p>
                <Status value={item.status} />
              </article>
            ))
          )}
        </section>
      )}
    </div>
  );
}
